/**
 * ISOLATED DATA MANAGEMENT
 * Complete state isolation between features
 * Each feature has its own independent state and data loaders
 */

import { useState, useCallback } from "react";

/**
 * Isolated Data Management Hook
 * Provides completely separate state for each feature
 */
export function useIsolatedDataManagement() {
  
  // FEATURE 1: Data Snapshot State (Independent)
  const [feature1Data, setFeature1Data] = useState({
    allDates: [],
    latestByMetro: {},
    scores: null,
    ranks: null,
    latestDate: "",
    chartData: []
  });
  const [feature1Loading, setFeature1Loading] = useState(false);
  const [feature1Error, setFeature1Error] = useState(null);

  // FEATURE 2: Best Performing State (Independent)
  const [feature2Data, setFeature2Data] = useState({
    allDates: [],
    latestByMetro: {},
    scores: null,
    ranks: null,
    latestDate: "",
    chartData: []
  });
  const [feature2Loading, setFeature2Loading] = useState(false);
  const [feature2Error, setFeature2Error] = useState(null);

  // FEATURE 4: New Markets TP State (Independent)
  const [feature4Data, setFeature4Data] = useState({
    allDates: [],
    latestByMetro: {},
    bestPotential: [],
    tpByMetro: {},
    tpByRegionId: {},
    latestDate: "",
    chartData: []
  });
  const [feature4Loading, setFeature4Loading] = useState(false);
  const [feature4Error, setFeature4Error] = useState(null);

  // SHARED UI STATE (Non-conflicting)
  // Default to empty so the Level dropdown can show a neutral "Select area level" placeholder
  const [areaType, setAreaType] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [selectedMetro, setSelectedMetro] = useState("");
  const [searchText, setSearchText] = useState("");
  const [view, setView] = useState("");
  const [minPopOnly, setMinPopOnly] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [currentChartMetric, setCurrentChartMetric] = useState("Price");

  // SHARED INDEX STATE (Non-conflicting)
  const [levelItems, setLevelItems] = useState({
    State: [], Metro: [], County: [], City: [], Zipcode: [], Neighborhood: []
  });
  const [crumbsByName, setCrumbsByName] = useState({});
  const [populationByCity, setPopulationByCity] = useState({});
  const [rawCityNames, setRawCityNames] = useState([]);

  // SHARED LINKS (Best Performing Linked) for Explore
  const [linksByName, setLinksByName] = useState({});
  const [linksByRegionId, setLinksByRegionId] = useState({});

  // FEATURE RESET FUNCTIONS
  const resetFeature1 = useCallback(() => {
    console.log("[ISOLATED_MANAGEMENT] Resetting Feature 1");
    setFeature1Data({
      allDates: [],
      latestByMetro: {},
      scores: null,
      ranks: null,
      latestDate: "",
      chartData: []
    });
    setFeature1Loading(false);
    setFeature1Error(null);
  }, []);

  const resetFeature2 = useCallback(() => {
    console.log("[ISOLATED_MANAGEMENT] Resetting Feature 2");
    setFeature2Data({
      allDates: [],
      latestByMetro: {},
      scores: null,
      ranks: null,
      latestDate: "",
      chartData: []
    });
    setFeature2Loading(false);
    setFeature2Error(null);
  }, []);

  const resetFeature4 = useCallback(() => {
    console.log("[ISOLATED_MANAGEMENT] Resetting Feature 4");
    setFeature4Data({
      allDates: [],
      latestByMetro: {},
      bestPotential: [],
      tpByMetro: {},
      tpByRegionId: {},
      latestDate: "",
      chartData: []
    });
    setFeature4Loading(false);
    setFeature4Error(null);
  }, []);

  // COMPLETE RESET FUNCTION
  const resetAllFeatures = useCallback((preserveAreaType = false) => {
    console.log("[ISOLATED_MANAGEMENT] Resetting all features", preserveAreaType ? "(preserving areaType)" : "");
    console.log("[ISOLATED_MANAGEMENT] Before reset - view:", view, "areaType:", areaType, "selectedMetro:", selectedMetro, "selectedName:", selectedName);
    resetFeature1();
    resetFeature2();
    resetFeature4();
    
    // Reset shared state
    if (!preserveAreaType) {
      // Return to neutral "no level selected" state
      setAreaType("");
    }
    setSelectedName("");
    setSelectedMetro("");
    setSearchText("");
    setView("");
    setMinPopOnly(false);
    setShowReport(false);
    setCurrentChartMetric("Price");
    setLevelItems({
      State: [], Metro: [], County: [], City: [], Zipcode: [], Neighborhood: []
    });
    setCrumbsByName({});
    setPopulationByCity({});
    setRawCityNames([]);
    console.log("[ISOLATED_MANAGEMENT] After reset - all state should be cleared");
  }, [resetFeature1, resetFeature2, resetFeature4, view, areaType, selectedMetro, selectedName]);

  // FEATURE SWITCHING FUNCTION
  const switchToFeature = useCallback((featureName) => {
    console.log(`[ISOLATED_MANAGEMENT] Switching to feature: ${featureName}`);

    // Reset ONLY the target feature's data to avoid cross-feature conflicts,
    // but keep other features' data intact so switching views doesn't break
    // already loaded snapshots (especially for County).
    if (featureName === "performance" || featureName === "performance-snapshot") {
      resetFeature2();
    } else if (featureName === "newMarketsTP" || featureName === "newMarketsTP-snapshot") {
      resetFeature4();
    } else {
      // Default to Feature 1 (metro / direct snapshot)
      resetFeature1();
    }

    // Preserve shared UI state (areaType, selectedName, etc.) and just switch view
    setView(featureName);

    console.log(`[ISOLATED_MANAGEMENT] Switched to feature: ${featureName}`);
  }, [resetFeature1, resetFeature2, resetFeature4]);

  // GET CURRENT FEATURE DATA
  const getCurrentFeatureData = useCallback(() => {
    switch (view) {
      case "":
        // Default view (empty) corresponds to Feature 1 direct selection
        return {
          data: feature1Data,
          loading: feature1Loading,
          error: feature1Error
        };
      case "metro":
      case "direct":
      case "metro-snapshot":
        return {
          data: feature1Data,
          loading: feature1Loading,
          error: feature1Error
        };
      case "performance":
      case "performance-snapshot":
        return {
          data: feature2Data,
          loading: feature2Loading,
          error: feature2Error
        };
      case "newMarketsTP":
      case "newMarketsTP-snapshot":
        return {
          data: feature4Data,
          loading: feature4Loading,
          error: feature4Error
        };
      default:
        // Treat unknown view as Feature 1 to avoid empty screen and allow direct selection snapshots
        console.log(`[ISOLATED_MANAGEMENT] Unknown view "${view}" - defaulting to Feature 1 data`);
        return {
          data: feature1Data,
          loading: feature1Loading,
          error: feature1Error
        };
    }
  }, [view, feature1Data, feature1Loading, feature1Error, feature2Data, feature2Loading, feature2Error, feature4Data, feature4Loading, feature4Error]);

  return {
    // Feature 1 State
    feature1Data,
    setFeature1Data,
    feature1Loading,
    setFeature1Loading,
    feature1Error,
    setFeature1Error,

    // Feature 2 State
    feature2Data,
    setFeature2Data,
    feature2Loading,
    setFeature2Loading,
    feature2Error,
    setFeature2Error,

    // Shared UI State
    areaType,
    setAreaType,
    selectedName,
    setSelectedName,
    selectedMetro,
    setSelectedMetro,
    searchText,
    setSearchText,
    view,
    setView,
    minPopOnly,
    setMinPopOnly,
    showReport,
    setShowReport,
    currentChartMetric,
    setCurrentChartMetric,

    // Shared Index State
    levelItems,
    setLevelItems,
    crumbsByName,
    setCrumbsByName,
    populationByCity,
    setPopulationByCity,
    rawCityNames,
    setRawCityNames,
    linksByName,
    setLinksByName,
    linksByRegionId,
    setLinksByRegionId,

    // FEATURE 4 State
    feature4Data,
    setFeature4Data,
    feature4Loading,
    setFeature4Loading,
    feature4Error,
    setFeature4Error,

    // Reset Functions
    resetFeature1,
    resetFeature2,
    resetFeature4,
    resetAllFeatures,

    // Feature Management
    switchToFeature,
    getCurrentFeatureData
  };
}
